import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { ReportingRequest, InvoicingSummaryResponse, ReportingComparison, InvoiceTypeKey, FlagTypeKey, TopFlaggedVendorTypeRow } from '../../types/reporting';

interface SummaryInput extends ReportingRequest {
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
    vendorNames?: string[];
    invoiceTypes?: InvoiceTypeKey[];
    locationIds?: string[];
    flagTypes?: FlagTypeKey[];
    flagMatch?: 'any' | 'exact';
    topFlaggedVendorTypeOffset?: number;
}

const getInvoicingSummary = async (
    sessionId: string,
    input: SummaryInput
): Promise<InvoicingSummaryResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/invoicing/summary`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

/** Later page of ranked vendor/type slices. Does not replace the first five. */
export const fetchTopFlaggedVendorTypes = async (
    sessionId: string,
    input: SummaryInput,
    offset: number,
): Promise<{ rows: TopFlaggedVendorTypeRow[]; hasMore: boolean }> => {
    const data = await getInvoicingSummary(sessionId, {
        ...input,
        includeComparison: false,
        comparison: undefined,
        topFlaggedVendorTypeOffset: offset,
    });
    return {
        rows: data.topFlaggedVendorTypes ?? [],
        hasMore: Boolean(data.topFlaggedVendorTypesHasMore),
    };
};

export const useInvoicingSummary = (
    startDate: string,
    endDate: string,
    locationId?: string,
    preset?: string,
    comparison?: ReportingComparison,
    enabled = true,
    vendorNames?: string[],
    invoiceTypes?: InvoiceTypeKey[],
    locationIds?: string[],
    flagTypes?: FlagTypeKey[],
    flagMatch?: 'any' | 'exact',
) => {
    const sessionContext = useContext(SessionContext);
    const scopedLocationIds = locationIds?.length ? locationIds : undefined;

    return useQuery({
        queryKey: ['reporting', 'invoicing', 'summary', startDate, endDate, locationId, scopedLocationIds, preset, comparison, vendorNames, invoiceTypes, flagTypes, flagMatch],
        queryFn: () => getInvoicingSummary(sessionContext?.sessionId ?? '', {
            startDate,
            endDate,
            locationId: scopedLocationIds ? undefined : locationId,
            locationIds: scopedLocationIds,
            preset,
            comparison,
            includeComparison: !!comparison,
            vendorNames: vendorNames?.length ? vendorNames : undefined,
            invoiceTypes: invoiceTypes?.length ? invoiceTypes : undefined,
            flagTypes: flagTypes?.length ? flagTypes : undefined,
            flagMatch: flagTypes?.length && flagMatch === 'exact' ? 'exact' : undefined,
        }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
